import { Box, Menu } from '@zillow/constellation-10';
import {
  IconArchiveOutline,
  IconInboxOutline,
  IconSpamFolderOutline,
  IconStar0Percent,
} from '@zillow/constellation-icons';
import type { Persona } from '../../types/persona';
import type { Brand } from '../../types/brand';
import { SatelliteConversationFolderTypeV2 } from '@zg-rentals/ts-satellite-client';
import { useEffect, useState } from 'react';
import FolderSelectorMenuItem from './FolderSelectorMenuItem';

type MenuItemConfig = {
  label: string;
  icon: React.ReactNode;
  folder: SatelliteConversationFolderTypeV2;
  isActive: boolean;
};

export type FolderActions = {
  onFolderChange: (folderOption: SatelliteConversationFolderTypeV2) => void;
  onMenuOpen?: () => void;
};

export type FolderSelectorProps = {
  persona?: Persona;
  brand?: Brand;
  selectedFolder: SatelliteConversationFolderTypeV2;
  actions: FolderActions;
};

export default function FolderSelector({
  persona = 'landlord',
  brand = 'zillow',
  selectedFolder,
  actions,
}: FolderSelectorProps) {
  const [folder, setFolder] = useState<SatelliteConversationFolderTypeV2>(selectedFolder);
  const [isActive, setIsActive] = useState(false);
  const { onFolderChange, onMenuOpen } = actions;
  const isZillowBrand = brand === 'zillow';
  const isPersonaRenter = persona === 'renter';

  useEffect(() => {
    setFolder(selectedFolder);
  }, [selectedFolder]);

  const label = (() => {
    if (folder === SatelliteConversationFolderTypeV2.ARCHIVED) {
      return 'Archived';
    } else if (folder === SatelliteConversationFolderTypeV2.SPAM) {
      return isZillowBrand && isPersonaRenter ? 'Spam' : 'Reported';
    } else if (folder === SatelliteConversationFolderTypeV2.FAVORITE) {
      return 'Starred';
    }
    return isZillowBrand && isPersonaRenter ? 'Recent' : 'Inbox';
  })();

  const getMenuItems = (): Array<MenuItemConfig> => {
    if (isPersonaRenter) {
      return [
        {
          label: isZillowBrand ? 'Recent' : 'Inbox',
          icon: <IconInboxOutline />,
          folder: SatelliteConversationFolderTypeV2.INBOX,
          isActive: isViewInbox,
        },
        {
          label: 'Archived',
          icon: <IconArchiveOutline />,
          folder: SatelliteConversationFolderTypeV2.ARCHIVED,
          isActive: isViewArchive,
        },
        {
          label: isZillowBrand ? 'Spam' : 'Reported',
          icon: <IconSpamFolderOutline />,
          folder: SatelliteConversationFolderTypeV2.SPAM,
          isActive: isViewSpam,
        },
      ];
    }

    return [
      {
        label: 'Inbox',
        icon: <IconInboxOutline />,
        folder: SatelliteConversationFolderTypeV2.INBOX,
        isActive: isViewInbox,
      },
      {
        label: 'Starred',
        icon: <IconStar0Percent />,
        folder: SatelliteConversationFolderTypeV2.FAVORITE,
        isActive: isViewFavorites,
      },
      {
        label: 'Archived',
        icon: <IconArchiveOutline />,
        folder: SatelliteConversationFolderTypeV2.ARCHIVED,
        isActive: isViewArchive,
      },
      {
        label: 'Reported',
        icon: <IconSpamFolderOutline />,
        folder: SatelliteConversationFolderTypeV2.SPAM,
        isActive: isViewSpam,
      },
    ];
  };

  const getHandleViewClickFn = (folderOption: SatelliteConversationFolderTypeV2) => {
    return () => {
      if (folderOption !== folder) {
        setFolder(folderOption);
        setIsActive(false);
        onFolderChange(folderOption);
      }
    };
  };

  const { isViewArchive, isViewInbox, isViewSpam, isViewFavorites } = (() => {
    return {
      isViewInbox: folder === SatelliteConversationFolderTypeV2.INBOX,
      isViewArchive: folder === SatelliteConversationFolderTypeV2.ARCHIVED,
      isViewSpam: folder === SatelliteConversationFolderTypeV2.SPAM,
      isViewFavorites: folder === SatelliteConversationFolderTypeV2.FAVORITE,
    };
  })();

  const handleTargetClick = () => {
    setIsActive(!isActive);

    if (!isActive && onMenuOpen) {
      onMenuOpen();
    }
  };

  const menuItems = getMenuItems();

  return (
    <Menu
      css={{ width: '355px', mdDown: { width: '200px' } }}
      dividers
      shouldAwaitInteractionResponse
      content={
        <Menu.Group aria-label="Folder Selector Menu">
          <Menu.Heading level={3} css={{ textTransform: 'capitalize' }}>
            View
          </Menu.Heading>
          <Box css={{ padding: 'default', mdDown: { padding: '0px' } }}>
            {menuItems.map(({ label, icon, folder, isActive }) => (
              <FolderSelectorMenuItem
                key={label}
                itemLabel={label}
                icon={icon}
                onItemClick={getHandleViewClickFn(folder)}
                isActive={isActive}
              />
            ))}
          </Box>
        </Menu.Group>
      }
      trigger={
        <Menu.Button onClick={handleTargetClick} data-testid="folder-selector-button">
          {label}
        </Menu.Button>
      }
    />
  );
}
